import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CommentIcon from '@mui/icons-material/Comment';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    SextforceAutoCampaignTipReply,
    SextforceAutoCampaignTipReplyMessageDetails,
} from '../../../../hooks/useSextforceAutoCampaignTipReply';
import useSextforceAutoCampaignTipReplyLogs, {
    SextforceAutoCampaignTipReplyLog,
} from '../../../../hooks/useSextforceAutoCampaignTipReplyLogs';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';

type Props = {
    subscriber: any;
    autoCampaignTipReply: SextforceAutoCampaignTipReply;
    messageDetailsList?: SextforceAutoCampaignTipReplyMessageDetails[];
    amount: number | null;
    open: boolean;
    onClose: () => void;
    theme: Theme;
};

type FanStats = {
    user: SextforceAutoCampaignTipReplyLog['user'];
    tipsCount: number;
    tipsTotal: number;
    repliesCount: number;
    replies: { label: string; count: number }[];
    lastTipDate: Date;
};

const SextforceAutoCampaignTipReplyTippersListDialog = (props: Props) => {
    const { subscriber, autoCampaignTipReply, messageDetailsList, amount, open, onClose, theme } = props;

    const { data: logs, isLoading: isLogsLoading } = useSextforceAutoCampaignTipReplyLogs(autoCampaignTipReply._id, amount);

    const [groupByFan, setGroupByFan] = useState(false);

    // Map each reply variant ID to a label ("Reply N" per tip amount, in creation order, or the variant's name)
    const variantLabels = useMemo(() => {
        const labels = new Map<string, string>();

        if (!messageDetailsList) {
            return labels;
        }

        // Group the variants by tip amount so the "Reply N" numbering is per tip amount
        const amounts = [...new Set(messageDetailsList.map(messageDetails => messageDetails.tipAmount))];

        amounts.forEach(tipAmount => {
            const variants = messageDetailsList
                .filter(messageDetails => messageDetails.tipAmount === tipAmount)
                .sort((a, b) => (a._id || '').localeCompare(b._id || ''));

            variants.forEach((messageDetails, index) => {
                if (messageDetails._id) {
                    labels.set(
                        messageDetails._id,
                        messageDetails.name && messageDetails.name.trim().length > 0 ? messageDetails.name : `Reply ${index + 1}`,
                    );
                }
            });
        });

        return labels;
    }, [messageDetailsList]);

    // Resolve the reply label for a log entry. Legacy logs without a messageDetailsId are attributed to the
    // oldest variant of the log's tip amount.
    const replyLabelForLog = (log: SextforceAutoCampaignTipReplyLog): string | null => {
        if (log.messageDetailsId) {
            return variantLabels.get(log.messageDetailsId) || null;
        }

        if (!messageDetailsList) {
            return null;
        }

        const oldestVariant = messageDetailsList
            .filter(messageDetails => messageDetails.tipAmount === log.tipAmount)
            .sort((a, b) => (a._id || '').localeCompare(b._id || ''))[0];

        return oldestVariant && oldestVariant._id ? variantLabels.get(oldestVariant._id) || null : null;
    };

    // Aggregate the logs per fan for the "Group by Fan" statistics view
    const fanStats: FanStats[] = useMemo(() => {
        if (!logs || logs.length === 0) {
            return [];
        }

        const statsByUserId = new Map<number, FanStats>();

        logs.forEach(log => {
            const existing = statsByUserId.get(log.user.id);

            const replyLabel = replyLabelForLog(log);

            if (existing) {
                existing.tipsCount += 1;
                existing.tipsTotal += log.tipAmount;
                existing.repliesCount += 1;

                if (replyLabel) {
                    const existingReply = existing.replies.find(reply => reply.label === replyLabel);

                    if (existingReply) {
                        existingReply.count += 1;
                    } else {
                        existing.replies.push({ label: replyLabel, count: 1 });
                    }
                }

                if (moment(log.notificationDate).isAfter(existing.lastTipDate)) {
                    existing.lastTipDate = log.notificationDate;
                }
            } else {
                statsByUserId.set(log.user.id, {
                    user: log.user,
                    tipsCount: 1,
                    tipsTotal: log.tipAmount,
                    repliesCount: 1,
                    replies: replyLabel ? [{ label: replyLabel, count: 1 }] : [],
                    lastTipDate: log.notificationDate,
                });
            }
        });

        // Sort by total tipped amount descending
        return Array.from(statsByUserId.values()).sort((a, b) => b.tipsTotal - a.tipsTotal);
    }, [logs, variantLabels, messageDetailsList]);

    const showReplyColumn = variantLabels.size > 0;

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown scroll="paper">
            <StyledCardTitleBar title={`Auto Tip Campaign Tippers${!isLogsLoading && logs && ` (${logs.length.toLocaleString()})`}`} />
            <DialogContent>
                <DialogContentText>
                    The following users have tipped the campaign <strong>{autoCampaignTipReply.name}</strong>
                    {amount && (
                        <span>
                            {' '}
                            for amount <strong>{dinero({ amount: Math.trunc(amount * 100), currency: 'USD' }).toFormat()}</strong>
                        </span>
                    )}
                    .
                </DialogContentText>
                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1} sx={{ mt: 1 }}>
                    {isLogsLoading && (
                        <Grid item xs={12} textAlign="center">
                            <Stack direction={'column'} spacing={1} alignItems={'center'} justifyContent={'center'}>
                                <CircularProgress />
                                <Typography variant="body1">Loading...</Typography>
                            </Stack>
                        </Grid>
                    )}
                    {!isLogsLoading && logs && logs.length === 0 && (
                        <Grid item xs={12} textAlign="center">
                            <Typography variant="body1" color={theme.palette.text.disabled}>
                                No tippers found
                            </Typography>
                        </Grid>
                    )}
                    {!isLogsLoading && logs && logs.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={groupByFan}
                                            onChange={(e, checked) => {
                                                setGroupByFan(checked);
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body2">Group by Fan (reply statistics per fan)</Typography>}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table
                                        width="100%"
                                        sx={{
                                            // no left/right padding on the first and last cells
                                            '& td:first-of-type, & th:first-of-type': {
                                                paddingLeft: 0,
                                            },
                                            // Bold header
                                            '& th': {
                                                fontWeight: 'bold',
                                            },
                                            // No wrapping
                                            whiteSpace: 'nowrap',
                                            // make last column sticky
                                            '& th, & td': {
                                                '&:last-child': {
                                                    position: 'sticky',
                                                    right: 0,
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            },
                                        }}
                                    >
                                        {groupByFan ? (
                                            <>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Username</TableCell>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell align="right">Tips</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                        {showReplyColumn && <TableCell>Replies Received</TableCell>}
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {fanStats.map((fan, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{fan.user.username}</TableCell>
                                                            <TableCell>{fan.user.name}</TableCell>
                                                            <TableCell align="right">{fan.tipsCount.toLocaleString()}</TableCell>
                                                            <TableCell align="right">
                                                                {dinero({
                                                                    amount: Math.trunc(fan.tipsTotal * 100),
                                                                    currency: 'USD',
                                                                }).toFormat()}
                                                            </TableCell>
                                                            {showReplyColumn && (
                                                                <TableCell>
                                                                    <Stack direction="row" spacing={0.5}>
                                                                        {fan.replies.map((reply, replyIndex) => (
                                                                            <Chip
                                                                                key={replyIndex}
                                                                                size="small"
                                                                                label={
                                                                                    reply.count > 1
                                                                                        ? `${reply.label} ×${reply.count}`
                                                                                        : reply.label
                                                                                }
                                                                            />
                                                                        ))}
                                                                    </Stack>
                                                                </TableCell>
                                                            )}
                                                            <TableCell align="center">
                                                                <Tooltip title="Go to user's profile" arrow placement="top">
                                                                    <IconButton
                                                                        component={Link}
                                                                        to={`https://onlyfans.com/${fan.user.username}`}
                                                                        target="_blank"
                                                                        color="secondary"
                                                                    >
                                                                        <AccountBoxIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </>
                                        ) : (
                                            <>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>User ID</TableCell>
                                                        <TableCell>Username</TableCell>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Tip</TableCell>
                                                        {showReplyColumn && <TableCell>Reply</TableCell>}
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {logs.map((log, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{moment(log.notificationDate).format('L LT')}</TableCell>
                                                            <TableCell align="right">{log.user.id}</TableCell>
                                                            <TableCell>{log.user.username}</TableCell>
                                                            <TableCell>{log.user.name}</TableCell>
                                                            <TableCell align="right">
                                                                {dinero({
                                                                    amount: Math.trunc(log.tipAmount * 100),
                                                                    currency: 'USD',
                                                                }).toFormat()}
                                                            </TableCell>
                                                            {showReplyColumn && (
                                                                <TableCell>
                                                                    {replyLabelForLog(log) ? (
                                                                        <Chip size="small" label={replyLabelForLog(log)} />
                                                                    ) : (
                                                                        <Typography variant="inherit" color={theme.palette.text.disabled}>
                                                                            -
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            )}
                                                            <TableCell align="center">
                                                                <Stack direction={'row'} spacing={0.5}>
                                                                    <Tooltip title="Go to message in OnlyFans chat" arrow placement="top">
                                                                        <IconButton
                                                                            component={Link}
                                                                            to={
                                                                                log.messageId
                                                                                    ? `https://onlyfans.com/my/chats/chat/${log.user.id}?firstId=${log.messageId}`
                                                                                    : ''
                                                                            }
                                                                            target="_blank"
                                                                            disabled={!log.messageId}
                                                                            color="primary"
                                                                        >
                                                                            <CommentIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Go to user's profile" arrow placement="top">
                                                                        <IconButton
                                                                            component={Link}
                                                                            to={`https://onlyfans.com/${log.user.username}`}
                                                                            target="_blank"
                                                                            color="secondary"
                                                                        >
                                                                            <AccountBoxIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </>
                                        )}
                                    </Table>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    backgroundColor: theme.palette.grey[100],
                }}
            >
                <Button
                    color="error"
                    size="medium"
                    onClick={() => {
                        handleClose({}, 'escapeKeyDown');
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SextforceAutoCampaignTipReplyTippersListDialog;
